import { useContext, useEffect, useState, useCallback } from 'react';
import { EntityContext, EntityTypes } from 'context/entityContext';
import trimTrendsByAllNull from '../helpers/trimTrendsByAllNull';
import normalizePeriodsToDate from '../helpers/normalizePeriodsToDate';
import useChartDipsParam from 'hooks/useChartDipsParam';
import removeOutliersPerTrend from '../helpers/removeOutliersPerTrend';
import { ISourceOfStreamsTrends } from '../SourceOfStreams/getDataToggleListItems';
import { CountryIds } from 'types/shared';
import { INormalizedPeriods } from '../helpers/normalizePeriodsToDate';

export interface ITrendDataWithCountry extends ISourceOfStreamsTrends {
  countryId?: CountryIds;
}

interface ITrendDataProps {
  trendData: ITrendDataWithCountry[];
  breakdown?: any;
  split?: EntityTypes;
  withOutliers?: boolean;
  interval?: string;
  componentId?: string;
}

/**
 * This hook prepares an object of trend data for usage in LineChart and DataToggleList
 *
 * @param trendData Trend data
 * @param breakdown Array of id breakdown ids
 * @param split The split to retrieve the data key
 * @param withOutliers if true don't apply chart dips algorithm
 * @return {Array} [Processed trend data, the inferred period type]
 */
function useTrendData({
  trendData,
  breakdown,
  split,
  withOutliers,
  interval,
  componentId,
}: ITrendDataProps): any {
  const [trends, setTrends] = useState<INormalizedPeriods[] | null>(null);
  const { compareMode, countryIds, entityIds, topic } =
    useContext(EntityContext);

  const isChartDipsOn = useChartDipsParam();

  const normalizeTrends = useCallback(
    (trends) => {
      return normalizePeriodsToDate({
        entities: trends,
        compareMode,
        entityIds,
        countryIds: countryIds as any,
        split,
      });
    },
    [compareMode, countryIds, entityIds, split]
  );

  useEffect(() => {
    if (!trendData || trendData.length === 0) return;
    let noOutliers;

    // “Downloads” and “Physical” are always low-volumed
    // and no not need trimming.
    if (
      (topic as any).includes('download') ||
      (topic as any).includes('physical') ||
      withOutliers
    ) {
      noOutliers = trendData;
    } else {
      noOutliers = removeOutliersPerTrend(
        trendData,
        split as EntityTypes,
        interval || '',
        isChartDipsOn
      );
    }

    const normalized = normalizeTrends(noOutliers);
    const trimmed = trimTrends(normalized);

    const sorted = sortTrends(trimmed);
    /** Added entity (its id and type) for TopLists component */
    const trendsWithId = !componentId
      ? sorted
      : sorted.map((trend: any) =>
          componentId
            ? Object.assign({}, trend, {
                entity: { id: componentId, type: 'worldwide' },
              })
            : trend
        );
    setTrends(trendsWithId);

    function trimTrends(trends: INormalizedPeriods[]): INormalizedPeriods[] {
      const [next] = trimTrendsByAllNull([trends]);
      return next;
    }

    function sortTrends(trends: INormalizedPeriods[]): INormalizedPeriods[] {
      return trends.sort(
        (trendA: INormalizedPeriods, trendB: INormalizedPeriods): number => {
          if (compareMode === 'entity') {
            return (
              entityIds.indexOf(trendA.entity.id) -
              entityIds.indexOf(trendB.entity.id)
            );
          }

          if (compareMode === 'country' && countryIds) {
            return (
              countryIds.indexOf(trendA.countryId as any) -
              countryIds.indexOf(trendB.countryId as any)
            );
          }

          if (breakdown) {
            return (
              breakdown.indexOf(trendA.breakdown) -
              breakdown.indexOf(trendB.breakdown)
            );
          }

          return 0;
        }
      );
    }
  }, [
    trendData,
    compareMode,
    countryIds,
    entityIds,
    breakdown,
    split,
    normalizeTrends,
    interval,
    topic,
    withOutliers,
    isChartDipsOn,
    componentId,
  ]);

  return trends;
}

export default useTrendData;
